// Signed receipts of every consent change, newest first (FR-21).
import { Anchor, Group, Loader, Table, Text, VisuallyHidden } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { SignedIn } from "../../shared/session/guards";
import { FieldBadges, useFormat } from "../../shared/ui/format";
import { FormError, WidePage } from "../../shared/ui/Page";

export function ReceiptsPage() {
  return (
    <SignedIn>
      <Receipts />
    </SignedIn>
  );
}

function Receipts() {
  const { t } = useTranslation();
  const f = useFormat();
  const receipts = useQuery({
    queryKey: ["receipts"],
    queryFn: () => unwrap(api.GET("/consents/receipts", { params: { query: { limit: 100 } } })),
  });
  const items = receipts.data?.items ?? [];
  return (
    <WidePage title={t("receipts.title")} intro={t("receipts.intro")}>
      {receipts.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : receipts.isError ? (
        <FormError message={errorMessage(receipts.error, t("error.generic"))} />
      ) : items.length === 0 ? (
        <Text c="dimmed">{t("receipts.empty")}</Text>
      ) : (
        <Table.ScrollContainer minWidth={560}>
          <Table striped verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("receipts.time")}</Table.Th>
                <Table.Th>{t("receipts.company")}</Table.Th>
                <Table.Th>{t("receipts.after")}</Table.Th>
                <Table.Th>{t("receipts.channel")}</Table.Th>
                <Table.Th>
                  <VisuallyHidden>{t("receipts.view")}</VisuallyHidden>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((r) => (
                <Table.Tr key={r.event_id} data-testid="receipt-row">
                  <Table.Td>{f.dateTime(r.created_at)}</Table.Td>
                  <Table.Td>{r.company.name}</Table.Td>
                  <Table.Td>
                    <FieldBadges fields={r.fields_after} />
                  </Table.Td>
                  <Table.Td>{f.channel(r.channel)}</Table.Td>
                  <Table.Td>
                    <Anchor
                      component={Link}
                      to={`/receipts/${r.event_id}`}
                      aria-label={t("receipts.view_for", { company: r.company.name, date: f.dateTime(r.created_at) })}
                    >
                      {t("receipts.view")}
                    </Anchor>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <Group>
        <Anchor component={Link} to="/verify-receipt" size="sm">
          {t("receipts.verify_link")}
        </Anchor>
      </Group>
    </WidePage>
  );
}
